import { useEffect, useMemo, useState } from "react";
import {
  Save, Megaphone, AlertTriangle, Info, Wrench, RefreshCw, Eye,
  CalendarClock, Power, MousePointerClick, Timer, X, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import api from "../lib/api";

// Convert a stored UTC ISO string → value usable inside <input type="datetime-local">.
// The input value is naive (no timezone). Backend treats naive ISO as Bangladesh time,
// so we shift UTC → BD here on load and pass back as-is on save.
function utcIsoToBdLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const bd = new Date(d.getTime() + 6 * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${bd.getUTCFullYear()}-${pad(bd.getUTCMonth() + 1)}-${pad(bd.getUTCDate())}T${pad(
      bd.getUTCHours()
    )}:${pad(bd.getUTCMinutes())}`;
  } catch {
    return "";
  }
}

const TYPE_OPTIONS = [
  {
    value: "maintenance",
    label: "Maintenance",
    sub: "Scheduled downtime · red",
    Icon: Wrench,
    swatch: ["#3b0a0a", "#7f1d1d", "#b91c1c"],
    chipBg: "#fee2e2",
    chipFg: "#991b1b",
  },
  {
    value: "warning",
    label: "Warning",
    sub: "Heads-up alert · amber",
    Icon: AlertTriangle,
    swatch: ["#3f1e07", "#78350f", "#b45309"],
    chipBg: "#fef3c7",
    chipFg: "#92400e",
  },
  {
    value: "info",
    label: "Info",
    sub: "Latest update · blue",
    Icon: Info,
    swatch: ["#020617", "#0c2540", "#0369a1"],
    chipBg: "#dbeafe",
    chipFg: "#1e40af",
  },
  {
    value: "promo",
    label: "Promo",
    sub: "Marketing announcement · green",
    Icon: Megaphone,
    swatch: ["#022c1e", "#064e3b", "#047857"],
    chipBg: "#d1fae5",
    chipFg: "#065f46",
  },
];

// Premium toggle switch (no extra dependencies — pure tailwind).
function ToggleSwitch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-400 ${
        checked ? "bg-emerald-500" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function StatBadge({ label, value, tone = "neutral" }) {
  const toneCls =
    tone === "live"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : tone === "off"
      ? "bg-gray-50 text-gray-500 border-gray-200"
      : tone === "scheduled"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-gray-50 text-gray-700 border-gray-200";
  return (
    <div className={`px-3 py-2 rounded-md border ${toneCls}`}>
      <div className="text-[9.5px] uppercase tracking-[1.6px] font-semibold opacity-70">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

export default function ConfigSiteNotice() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // 1s tick — re-evaluates the "Live/Scheduled/Off" status badge so it updates
  // when the start/end window crosses without admin reload.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/site-notice");
      setForm({
        enabled: !!data.enabled,
        type: data.type || "maintenance",
        title: data.title || "",
        message: data.message || "",
        start_at_bd: utcIsoToBdLocalInput(data.start_at),
        end_at_bd: utcIsoToBdLocalInput(data.end_at),
        dismissible: data.dismissible !== false,
        show_countdown: data.show_countdown !== false,
        version: data.version || 0,
        updated_at: data.updated_at || "",
        updated_by: data.updated_by || "",
      });
    } catch (e) {
      toast.error("Failed to load site notice config");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      setRefreshing(true);
      try {
        const { data } = await api.get("/site-notice");
        setForm({
          enabled: !!data.enabled,
          type: data.type || "maintenance",
          title: data.title || "",
          message: data.message || "",
          start_at_bd: utcIsoToBdLocalInput(data.start_at),
          end_at_bd: utcIsoToBdLocalInput(data.end_at),
          dismissible: data.dismissible !== false,
          show_countdown: data.show_countdown !== false,
          version: data.version || 0,
          updated_at: data.updated_at || "",
          updated_by: data.updated_by || "",
        });
      } catch (e) {
        toast.error("Failed to load site notice config");
      } finally {
        setRefreshing(false);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        enabled: !!form.enabled,
        type: form.type,
        title: form.title || "",
        message: form.message || "",
        start_at: form.start_at_bd || null,
        end_at: form.end_at_bd || null,
        dismissible: !!form.dismissible,
        show_countdown: !!form.show_countdown,
      };
      await api.put("/site-notice", payload);
      toast.success("Site notice saved — live within ~10s on every panel");
      await load();
    } catch (e) {
      toast.error("Failed to save site notice");
    } finally {
      setLoading(false);
    }
  };

  const opt = useMemo(() => {
    if (!form) return TYPE_OPTIONS[0];
    return TYPE_OPTIONS.find((o) => o.value === form.type) || TYPE_OPTIONS[0];
  }, [form]);

  const scheduleSummary = useMemo(() => {
    if (!form) return { tone: "off", text: "Off" };
    if (!form.enabled) return { tone: "off", text: "Disabled" };
    const startTs = form.start_at_bd
      ? Date.parse(form.start_at_bd.replace(" ", "T") + ":00+06:00")
      : null;
    const endTs = form.end_at_bd
      ? Date.parse(form.end_at_bd.replace(" ", "T") + ":00+06:00")
      : null;
    if (startTs && nowTs < startTs) return { tone: "scheduled", text: "Scheduled · pending start" };
    if (endTs && nowTs > endTs) return { tone: "off", text: "Window passed" };
    return { tone: "live", text: "Live · visible now" };
  }, [form, nowTs]);

  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-10 text-center text-sm text-gray-500">
        Loading site notice configuration…
      </div>
    );
  }

  const OptIcon = opt.Icon;

  // Premium preview gradient — uses the selected type's swatch directly.
  const previewBg = `linear-gradient(95deg,${opt.swatch[0]} 0%,${opt.swatch[1]} 35%,${opt.swatch[2]} 65%,${opt.swatch[1]} 100%)`;

  return (
    <div data-testid="config-site-notice-page">
      <PageHeader
        title="Configuration"
        subtitle="Site Notice · top-of-page banner shown across site / agent / affiliate panels with Bangladesh-time scheduling"
      />
      <ConfigTabs />

      {/* Premium header card */}
      <div className="mb-5 rounded-md border border-gray-200 bg-gradient-to-br from-white to-gray-50/60 overflow-hidden">
        <div className="px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-md flex items-center justify-center text-white shadow-sm flex-shrink-0"
              style={{ background: previewBg }}
            >
              <Sparkles size={20} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[2px] text-emerald-600 font-bold">
                Site Notice Banner
              </div>
              <div className="text-lg font-semibold text-gray-900 mt-0.5">
                Broadcast a top-of-page announcement to every user
              </div>
              <div className="text-xs text-gray-500 mt-1 leading-relaxed max-w-2xl">
                Schedule maintenance windows, push warnings, or run a promo — all live within ~10 seconds across site, agent and affiliate panels. Users can dismiss the notice for the current session; a fresh page load shows it again.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 md:gap-2.5 w-full md:w-auto md:min-w-[360px]">
            <StatBadge label="Status" value={scheduleSummary.text} tone={scheduleSummary.tone} />
            <StatBadge label="Version" value={`v${form.version || 0}`} tone="neutral" />
            <StatBadge label="Type" value={opt.label} tone="neutral" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* ── Editor (2 cols) ─────────────────────────────────────── */}
        <div className="xl:col-span-2 space-y-5">
          {/* Master toggle */}
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Power size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Master switch
              </div>
            </div>
            <div className="px-5 py-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">Enable site notice</div>
                <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  Off · banner hidden everywhere. On · banner shown when the schedule window is active.
                </div>
              </div>
              <ToggleSwitch
                checked={form.enabled}
                onChange={(v) => set("enabled", v)}
                testid="cfg-notice-enabled"
              />
            </div>
          </div>

          {/* Type selector */}
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Megaphone size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Notice type
              </div>
            </div>
            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {TYPE_OPTIONS.map((o) => {
                const Cmp = o.Icon;
                const active = form.type === o.value;
                const grad = `linear-gradient(135deg,${o.swatch[0]} 0%,${o.swatch[1]} 50%,${o.swatch[2]} 100%)`;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => set("type", o.value)}
                    data-testid={`cfg-notice-type-${o.value}`}
                    className={`group relative overflow-hidden rounded-md border text-left transition-all duration-200 ${
                      active
                        ? "border-transparent shadow-sm ring-2 ring-offset-1 ring-gray-900"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                    }`}
                  >
                    <div
                      className="absolute inset-0 transition-opacity"
                      style={{ background: grad, opacity: active ? 1 : 0.06 }}
                      aria-hidden
                    />
                    <div className="relative px-3 py-3">
                      <div
                        className={`inline-flex w-8 h-8 items-center justify-center rounded-md mb-2 ${
                          active ? "bg-white/15" : ""
                        }`}
                        style={{
                          background: active ? "rgba(255,255,255,0.18)" : o.chipBg,
                          color: active ? "#ffffff" : o.chipFg,
                        }}
                      >
                        <Cmp size={15} strokeWidth={2.2} />
                      </div>
                      <div className={`text-sm font-semibold ${active ? "text-white" : "text-gray-900"}`}>
                        {o.label}
                      </div>
                      <div className={`text-[10.5px] mt-0.5 ${active ? "text-white/75" : "text-gray-500"}`}>
                        {o.sub}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Info size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Notice content
              </div>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  Title (optional)
                </label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Scheduled Maintenance"
                  className="mt-2 w-full h-10 px-3.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                  data-testid="cfg-notice-title"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  Message <span className="text-emerald-600 normal-case tracking-normal font-normal">· Bengali + English supported</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="আগামীকাল রাত ৩টা হতে ৪টা পর্যন্ত আপডেট চলবে। এই সময় ওয়েবসাইট ব্যবহার করতে পারবেন না।"
                  rows={3}
                  className="mt-2 w-full px-3.5 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition leading-relaxed"
                  data-testid="cfg-notice-message"
                />
                <div className="text-[11px] text-gray-400 mt-1.5">
                  Tip: leave blank to use only the Title. Multi-line text wraps gracefully on every device.
                </div>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <CalendarClock size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Schedule <span className="text-gray-400 normal-case tracking-normal">· Bangladesh time (UTC+6)</span>
              </div>
            </div>
            <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  Start at <span className="text-gray-400 normal-case tracking-normal">· optional</span>
                </label>
                <input
                  type="datetime-local"
                  value={form.start_at_bd}
                  onChange={(e) => set("start_at_bd", e.target.value)}
                  className="mt-2 w-full h-10 px-3.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                  data-testid="cfg-notice-start-at"
                />
                <div className="text-[11px] text-gray-400 mt-1.5">
                  Blank · show immediately when enabled.
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  End at <span className="text-gray-400 normal-case tracking-normal">· optional</span>
                </label>
                <input
                  type="datetime-local"
                  value={form.end_at_bd}
                  onChange={(e) => set("end_at_bd", e.target.value)}
                  className="mt-2 w-full h-10 px-3.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                  data-testid="cfg-notice-end-at"
                />
                <div className="text-[11px] text-gray-400 mt-1.5">
                  Blank · stays visible until you disable it.
                </div>
              </div>
            </div>
          </div>

          {/* Behaviour options */}
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Timer size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Behaviour
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <MousePointerClick size={16} className="text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Dismissible by user</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      Shows an &quot;X&quot; button. Dismissal is session-only — a page reload re-shows the banner.
                    </div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={form.dismissible}
                  onChange={(v) => set("dismissible", v)}
                  testid="cfg-notice-dismissible"
                />
              </div>
              <div className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Timer size={16} className="text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Show live countdown</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      Displays &quot;Starts in HH:MM:SS&quot; before the window, then &quot;Ends in HH:MM:SS&quot; while live.
                    </div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={form.show_countdown}
                  onChange={(v) => set("show_countdown", v)}
                  testid="cfg-notice-countdown"
                />
              </div>
            </div>
          </div>

          {/* Save bar */}
          <div className="sticky bottom-0 -mx-2 sm:mx-0 bg-white border border-gray-200 rounded-md shadow-sm">
            <div className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-[12px] text-gray-500">
                {form.version > 0 ? (
                  <>
                    Last saved by <b className="text-gray-800">{form.updated_by || "—"}</b>
                    <span className="text-gray-300 mx-1.5">·</span>
                    <span className="font-mono">v{form.version}</span>
                  </>
                ) : (
                  <span className="text-gray-400 italic">Not saved yet</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={load}
                  disabled={refreshing}
                  className="h-10 px-4 inline-flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 rounded-md text-sm text-gray-700 font-medium disabled:opacity-50"
                  data-testid="cfg-notice-refresh"
                >
                  <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
                  Reload
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="h-10 px-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md text-sm font-semibold shadow-sm"
                  data-testid="cfg-notice-save"
                >
                  <Save size={14} />
                  {loading ? "Saving…" : "Save & broadcast"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Live preview (1 col) ────────────────────────────────── */}
        <div className="xl:col-span-1">
          <div className="bg-white border border-gray-200 rounded-md xl:sticky xl:top-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-gray-500" />
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  Live preview
                </div>
              </div>
              <span
                className={`text-[10px] uppercase tracking-[1.4px] font-bold px-2 py-0.5 rounded-full border ${
                  scheduleSummary.tone === "live"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : scheduleSummary.tone === "scheduled"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-gray-50 text-gray-500 border-gray-200"
                }`}
              >
                {scheduleSummary.text}
              </span>
            </div>
            <div className="p-4 bg-gradient-to-br from-gray-50/60 to-white space-y-3">
              {/* Banner replica */}
              {form.enabled ? (
                <div
                  data-testid="cfg-notice-preview"
                  style={{
                    position: "relative",
                    background: previewBg,
                    color: "#ffffff",
                    padding: "12px 14px",
                    borderRadius: 6,
                    boxShadow: "0 8px 24px -8px rgba(15,23,42,0.35)",
                    fontFamily:
                      "'Hind Siliguri','Noto Sans Bengali',-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
                    overflow: "hidden",
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "radial-gradient(60% 120% at 0% 50%, rgba(255,255,255,0.10), transparent 60%)",
                      pointerEvents: "none",
                    }}
                  />
                  <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 9px 4px 8px",
                        borderRadius: 999,
                        background: "rgba(0,0,0,0.30)",
                        border: "1px solid rgba(255,255,255,0.25)",
                        fontSize: 9.5,
                        letterSpacing: "1.4px",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.95)",
                      }}
                    >
                      <OptIcon size={11} strokeWidth={2.4} />
                      {opt.label}
                    </span>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.5 }}>
                      {form.title ? (
                        <strong style={{ marginRight: 6 }}>{form.title} ·</strong>
                      ) : null}
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {form.message || (
                          <em style={{ opacity: 0.75 }}>
                            (Empty message — type something on the left)
                          </em>
                        )}
                      </span>
                    </div>
                    {form.dismissible ? (
                      <span
                        aria-hidden
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          background: "rgba(0,0,0,0.30)",
                          border: "1px solid rgba(255,255,255,0.20)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "rgba(255,255,255,0.85)",
                        }}
                      >
                        <X size={12} strokeWidth={2.4} />
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-400 italic px-3 py-10 text-center border border-dashed border-gray-200 rounded-md bg-white">
                  Master switch is OFF — turn it on to preview.
                </div>
              )}

              {/* Helpful copy */}
              <div className="text-[11.5px] text-gray-500 leading-relaxed bg-gray-50/70 border border-gray-100 rounded-md px-3 py-2.5">
                <div className="font-semibold text-gray-700 mb-1">How it behaves</div>
                <ul className="space-y-1 pl-3 list-disc marker:text-gray-300">
                  <li>Polled every <b className="text-gray-700">10 seconds</b> on every panel.</li>
                  <li>Dismissal is <b className="text-gray-700">session-only</b> — a reload re-shows it.</li>
                  <li>Schedule is interpreted in <b className="text-gray-700">Bangladesh time (UTC+6)</b>.</li>
                  <li>Each save bumps the version → old dismissals reset automatically.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
